/**
 * PSV-1906 — season info for the entry screen: current season number,
 * countdown to the Monday-00:00-UTC rollover, current leader and the
 * previous season's champion (hall of fame, newest entry).
 *
 * Reads the `Season` rows via a one-shot collection-form
 * DataSubscription (same raw pattern as matchConfig — the platform
 * pushes current state as the initial Overwrite). Failure → null; the
 * entry screen just skips the line.
 */

import { Commands, type PlayServClient } from 'playserv-sdk';

import { seasonEndsAt, seasonNumber } from '../../server/shared/seasons';

interface SeasonRow {
  Num?: number;
  TopName?: string;
  TopGems?: number;
}

export interface SeasonInfo {
  season: number;
  endsAt: number;
  leaderLine: string | null;
  lastChampionLine: string | null;
}

const READ_TIMEOUT_MS = 3_000;

export async function loadSeasonInfo(client: PlayServClient): Promise<SeasonInfo | null> {
  // PSV-2016 #5 — public raw() instead of the @internal _connection();
  // the try/catch below absorbs NotReadyError into the null fallback.
  const conn = client.raw();
  const now = Date.now();
  const season = seasonNumber(now);
  const base: SeasonInfo = {
    season,
    endsAt: seasonEndsAt(season),
    leaderLine: null,
    lastChampionLine: null,
  };
  try {
    const { requestId, promise } = conn.requestWithId(Commands.DataSubscriptionRequest, {
      Query: 'Season { Num TopName TopGems }',
      Variables: {},
    });
    const rows = await new Promise<SeasonRow[] | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), READ_TIMEOUT_MS);
      const off = conn.onSystemEvent('DataSubscriptionUpdate', (payload) => {
        const frame = payload as { RequestId?: unknown; Data?: unknown };
        if (frame.RequestId !== requestId) return;
        clearTimeout(timer);
        off();
        const body =
          typeof frame.Data === 'object' && frame.Data !== null && 'Season' in (frame.Data as object)
            ? (frame.Data as { Season?: unknown }).Season
            : frame.Data;
        resolve(Array.isArray(body) ? (body as SeasonRow[]) : null);
      });
      void promise.catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
    });
    if (rows === null) return base;
    const current = rows.find((r) => r.Num === season);
    if (current !== undefined && typeof current.TopName === 'string' && current.TopName !== '') {
      base.leaderLine = `Leader: ${current.TopName} (${current.TopGems ?? 0} ◆)`;
    }
    const prev = rows.find((r) => r.Num === season - 1);
    if (prev !== undefined && typeof prev.TopName === 'string' && prev.TopName !== '') {
      base.lastChampionLine = `Season ${season - 1} champion: ${prev.TopName}`;
    }
    return base;
  } catch {
    return base;
  }
}

/** "3d 12h" / "5h 20m" countdown text. */
export function formatCountdown(endsAt: number, now: number): string {
  const left = Math.max(0, endsAt - now);
  const d = Math.floor(left / 86_400_000);
  const h = Math.floor((left % 86_400_000) / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
}
